import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import ShieldIcon from '@material-ui/icons/Security';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { spassuCodeApiRef } from '../../api';
import { useProjectRole } from '../../hooks/useProjectRole';
import { RoleBadge } from '../RoleBadge';
import { AddGlobalAdminDialog } from '../AddGlobalAdminDialog';
import type { ProjectMember } from '../../api/types';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  entityCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  selfChip: {
    marginLeft: theme.spacing(1),
    padding: `0 ${theme.spacing(0.5)}px`,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    fontSize: '0.65rem',
    borderRadius: 4,
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  tempWarning: {
    color: theme.palette.warning.main,
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  healthBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    fontSize: '0.875rem',
  },
  healthIcon: {
    flexShrink: 0,
    marginTop: 2,
  },
  description: {
    marginBottom: theme.spacing(2),
  },
  dialogWarning: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
}));

/**
 * Parse a backend error message and translate well-known codes to friendlier
 * Portuguese messages. Falls back to the raw message.
 */
function translateBackendError(message: string): string {
  if (/last global admin/i.test(message)) {
    return 'Não é possível remover o último administrador global. O sistema precisa de ao menos 1 admin ativo.';
  }
  if (
    /you do not have permission/i.test(message) ||
    /forbidden/i.test(message)
  ) {
    return 'Você não tem permissão para essa ação.';
  }
  return message;
}

export const GlobalAdminsPanel = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const { isAdmin, userId, refresh: refreshRoles } = useProjectRole();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmTarget, setConfirmTarget] = useState<ProjectMember | null>(
    null,
  );
  const [removing, setRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'error' | 'success';
  } | null>(null);

  const { value: members, loading } = useAsync(
    () => api.getProjectMembers('*'),
    [refreshKey],
  );

  const admins = useMemo(
    () => (members ?? []).filter(m => m.role === 'admin'),
    [members],
  );

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
    refreshRoles();
  }, [refreshRoles]);

  const handleConfirmRemove = useCallback(async () => {
    if (!confirmTarget) return;
    setRemoving(true);
    try {
      await api.removeProjectMember(
        '*',
        confirmTarget.entityRef,
        'admin',
        null,
      );
      setSnackbar({
        severity: 'success',
        message: `${confirmTarget.entityRef} removido como admin global.`,
      });
      setConfirmTarget(null);
      handleRefresh();
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : String(err);
      setSnackbar({
        severity: 'error',
        message: translateBackendError(raw),
      });
      // Keep dialog open on "last admin" error so user sees the context;
      // close on any other error.
      if (!/last global admin/i.test(raw)) {
        setConfirmTarget(null);
      }
      // Refresh to re-sync state (other admin may have removed someone)
      handleRefresh();
    } finally {
      setRemoving(false);
    }
  }, [api, confirmTarget, handleRefresh]);

  const isExpiringSoon = (expiresAt: string | null): boolean => {
    if (!expiresAt) return false;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000;
  };

  if (!isAdmin) {
    return (
      <Typography variant="body2" color="textSecondary">
        Apenas administradores globais podem visualizar esta seção.
      </Typography>
    );
  }

  if (loading) {
    return <Typography>Carregando administradores globais...</Typography>;
  }

  const showLowAdminWarning = admins.length < 2;
  const isSelf = (member: ProjectMember) => member.entityRef === userId;
  const isLastAdmin = admins.length <= 1;

  return (
    <div>
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.description}
      >
        Administradores globais têm acesso total ao Spassu Code — gerenciam
        membros, políticas, skills e visualizam todos os projetos. Mantenha ao
        menos 2 administradores ativos para evitar lockout em caso de
        indisponibilidade.
      </Typography>

      {showLowAdminWarning && (
        <Box className={classes.healthBanner} role="alert">
          <WarningIcon className={classes.healthIcon} fontSize="small" />
          <div>
            <strong>Atenção:</strong> o sistema possui apenas {admins.length}{' '}
            administrador
            {admins.length === 1 ? '' : 'es'} global
            {admins.length === 1 ? '' : 'is'}. Recomendamos manter ao menos 2
            para redundância.
          </div>
        </Box>
      )}

      <div className={classes.header}>
        <div className={classes.title}>
          <ShieldIcon fontSize="small" color="secondary" />
          <Typography variant="h6" component="h2">
            Administradores Globais ({admins.length})
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Adicionar Admin
        </Button>
      </div>

      <Table size="small" aria-label="Administradores globais do Spassu Code">
        <TableHead>
          <TableRow>
            <TableCell>Entidade</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Concedido por</TableCell>
            <TableCell>Desde</TableCell>
            <TableCell>Expiração</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {admins.map(member => {
            const self = isSelf(member);
            const disableRemove = isLastAdmin;
            let removeTooltip = 'Remover admin global';
            if (disableRemove) {
              removeTooltip = 'Não é possível remover o último admin';
            } else if (self) {
              removeTooltip = 'Remover a si mesmo como admin global';
            }
            return (
              <TableRow key={member.id}>
                <TableCell>
                  <div className={classes.entityCell}>
                    {member.entityKind === 'Group' ? (
                      <Tooltip title="Grupo">
                        <GroupIcon fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Usuário">
                        <PersonIcon fontSize="small" />
                      </Tooltip>
                    )}
                    <span>{member.entityRef}</span>
                    {self && (
                      <span className={classes.selfChip} aria-label="Você">
                        Você
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <RoleBadge role={member.role} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    {member.assignedBy}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(member.createdAt).toLocaleDateString('pt-BR')}
                </TableCell>
                <TableCell>
                  {member.expiresAt ? (
                    <span>
                      {new Date(member.expiresAt).toLocaleDateString('pt-BR')}
                      {isExpiringSoon(member.expiresAt) && (
                        <span className={classes.tempWarning}>
                          <WarningIcon style={{ fontSize: 14 }} />
                          Expira em breve!
                        </span>
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={removeTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={disableRemove}
                        aria-label={`Remover ${member.entityRef} como administrador global`}
                        onClick={() => setConfirmTarget(member)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {admins.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="textSecondary">
                  Nenhum administrador global configurado. O sistema precisa ao
                  menos 1 admin.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <AddGlobalAdminDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={() => {
          setSnackbar({
            severity: 'success',
            message: 'Administrador global adicionado com sucesso.',
          });
          handleRefresh();
        }}
      />

      {/* Confirmation dialog (replaces window.confirm) */}
      <Dialog
        open={confirmTarget !== null}
        onClose={() => (removing ? undefined : setConfirmTarget(null))}
        aria-labelledby="remove-admin-title"
        aria-describedby="remove-admin-description"
      >
        <DialogTitle id="remove-admin-title">
          Remover administrador global?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-admin-description">
            {confirmTarget && isSelf(confirmTarget) ? (
              <>
                <span className={classes.dialogWarning}>
                  Você está removendo a si mesmo.
                </span>{' '}
                Após confirmar, você perderá acesso de administrador e não
                poderá gerenciar admins globais nem políticas. Certifique-se de
                que há outro administrador disponível.
              </>
            ) : (
              <>
                <strong>{confirmTarget?.entityRef}</strong> perderá acesso total
                ao Spassu Code — todos os projetos, políticas, skills e audit
                log.
              </>
            )}
            <br />
            <br />
            Esta ação é registrada no audit log e não pode ser desfeita pela UI
            (apenas concedendo admin novamente).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={removing}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="secondary"
            variant="contained"
            disabled={removing}
          >
            {removing ? 'Removendo...' : 'Remover admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error feedback (replaces window.alert) */}
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </div>
  );
};
